import { useState } from 'react';
import { Gamepad2, ExternalLink, Coins } from 'lucide-react';
import { motion } from 'framer-motion';
import NavShell from '../components/NavShell';
import OfferDetailModal, { StoreQRModal, isMobileUserAgent, gameToDetailItem } from '../components/OfferDetailModal';
import { PlatformTag } from '../components/PlatformTag';
import { useGames } from '../hooks/useGames';
import { openGokartClick } from '../lib/gokartOpen';
import type { DisplayGame } from '@swipeopinion/shared';

function GameCard({ game, onSelect }: { game: DisplayGame; onSelect: (g: DisplayGame) => void }) {
  return (
    <div
      onClick={() => onSelect(game)}
      className="group bg-white rounded-2xl shadow-sm hover:shadow-lg hover:border-primary/30 transition-all duration-300 overflow-hidden cursor-pointer flex flex-col"
    >
      {/* Image — best-effort banner, else app icon. */}
      <div className="relative h-40 bg-gray-100 overflow-hidden">
        {(game.bannerUrl || game.imageUrl) ? (
          <img src={game.bannerUrl || game.imageUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Gamepad2 className="w-12 h-12 text-gray-300" />
          </div>
        )}
        {game.isNew && (
          <span className="absolute top-3 left-3 bg-[#F97316] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">New</span>
        )}
        <span className="absolute top-3 right-3 bg-[#10B981]/90 text-white text-xs font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1">
          <Coins className="w-3 h-3" /> Up to {game.maxPoints} pts
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-medium text-[#6B7280] bg-gray-100 px-2 py-0.5 rounded-full">
            {game.category}
          </span>
          <span className="text-[10px] font-medium text-[#6B7280] bg-gray-100 px-2 py-0.5 rounded-full">
            <PlatformTag platform={game.device} />
          </span>
        </div>

        <h3 className="text-base font-bold font-display text-[#111827] mb-1 line-clamp-1 group-hover:text-primary transition-colors">
          {game.title}
        </h3>
        {game.cardText && (
          <p className="text-xs text-[#6B7280] line-clamp-2 mb-3">{game.cardText}</p>
        )}

        <div className="mt-auto">
          {/* "Play Now" opens the detail modal — same path as clicking the card.
              The actual store redirect / QR is triggered from the modal's CTA. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onSelect(game); }}
            className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-primary/10 text-primary text-sm font-semibold hover:bg-primary hover:text-white transition-colors"
          >
            Play Now <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

type PlatformFilter = 'all' | 'android' | 'ios';

export default function GamesPage() {
  // Tabs are restored now that the API call passes `device_platform=all` and
  // returns both Android and iOS catalogs. Filtering happens client-side
  // against `game.device` so the user can flip tabs without a refetch.
  const { games: allGames, total, loading: isLoading, loadingMore, error, hasMore, loadMore } = useGames();
  const [platform, setPlatform] = useState<PlatformFilter>('all');
  const [selectedGame, setSelectedGame] = useState<DisplayGame | null>(null);
  const [qr, setQr] = useState<{ url: string; title: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const hasIosGames = allGames.some((g) => g.device.toLowerCase() === 'ios');

  const games = platform === 'all'
    ? allGames
    : allGames.filter((g) => {
        const d = g.device.toLowerCase();
        return d === platform || d === 'all';
      });

  const handlePlay = (game: DisplayGame) => {
    setNotice(null);
    // GoKart games carry NO static redirectUrl — it's resolved live via the
    // click handshake. Branch BEFORE the redirectUrl guard (which would no-op
    // every GoKart game). openGokartClick handles the popup-safe open +
    // mobile/desktop split internally (mirrors the Besitos UX below).
    if (game.source === 'gokart') {
      const gokartOfferId = game.id.replace(/^gokart:/, '');
      setSelectedGame(null);
      void openGokartClick(gokartOfferId, game.title, {
        onQr: (url, title) => setQr({ url, title }),
        onMessage: (msg) => setNotice(msg),
      });
      return;
    }
    // Besitos — static redirectUrl, unchanged UX.
    if (!game.redirectUrl) return;
    if (isMobileUserAgent()) {
      window.open(game.redirectUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Desktop — show a QR so the member can scan with their phone.
      setSelectedGame(null);
      setQr({ url: game.redirectUrl, title: game.title });
    }
  };

  return (
    <NavShell>
      <motion.main initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="px-4 sm:px-6 py-6 space-y-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1e1b4b] to-[#312e81] p-6 sm:p-8 text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="relative flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-display">Games</h1>
              <p className="text-purple-200 mt-1 text-sm">Play games and earn points for hitting milestones</p>
            </div>
            {!isLoading && total > 0 && (
              <div className="flex gap-3 flex-wrap">
                <div className="bg-white/15 backdrop-blur-sm rounded-full px-4 py-2.5">
                  <span className="text-xl font-bold tabular-nums">{total}</span>
                  <span className="text-purple-200 text-sm ml-1">Available</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Platform filter — iOS tab is hidden when no iOS games exist so we
            don't surface an empty pill. */}
        {!isLoading && allGames.length > 0 && (
          <div className="flex gap-2" data-testid="platform-tabs">
            {(['all', 'android', ...(hasIosGames ? ['ios' as const] : [])] as PlatformFilter[]).map((p) => {
              const active = platform === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlatform(p)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    active
                      ? 'bg-primary text-white'
                      : 'bg-white text-[#6B7280] border border-[#E5E7EB] hover:border-primary/30'
                  }`}
                >
                  <PlatformTag platform={p} />
                </button>
              );
            })}
          </div>
        )}

        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm h-72 animate-pulse" />
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-16 text-[#EF4444]">Failed to load games. Please try again.</div>
        )}

        {notice && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center text-sm text-amber-800" data-testid="game-notice">
            {notice}
          </div>
        )}

        {!isLoading && !error && games.length === 0 && (
          <div className="text-center py-16">
            <Gamepad2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-[#6B7280] font-medium">No games available right now.</p>
            <p className="text-sm text-[#6B7280] mt-1">Check back soon.</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {games.map((game) => (
            <GameCard key={game.id} game={game} onSelect={setSelectedGame} />
          ))}
        </div>

        {/* Running count of the loaded catalog vs the real total (grows on Load more). */}
        {!isLoading && !error && allGames.length > 0 && (
          <p className="text-center text-sm text-[#6B7280]">
            Showing {allGames.length} of {total} games
          </p>
        )}

        {!isLoading && !error && hasMore && (
          <div className="pt-2 flex justify-center">
            <button
              type="button"
              onClick={() => { void loadMore(); }}
              disabled={loadingMore}
              className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-dark font-medium disabled:opacity-50"
              data-testid="games-load-more"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </motion.main>

      {selectedGame && (
        <OfferDetailModal
          item={gameToDetailItem(selectedGame)}
          onClose={() => setSelectedGame(null)}
          onCta={() => handlePlay(selectedGame)}
        />
      )}
      {qr && (
        <StoreQRModal url={qr.url} title={qr.title} onClose={() => setQr(null)} />
      )}
    </NavShell>
  );
}
